import { Direction } from '@angular/cdk/bidi';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    HostBinding,
    inject,
    input,
    NgZone,
    OnDestroy,
    output,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';
import { MatChipGrid } from '@angular/material/chips';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { EnpsComponent } from '@common/components/base/enps-component';
import { IEquals } from '@common/interfaces/i-equals';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { Store } from '@ngrx/store';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectDateLocale } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { AppService } from '@suite/app.service';
import { type Moment } from 'moment-timezone';
import { Subject } from 'rxjs';

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class EnpsControlComponent<T extends IEquals<T> | string | number | boolean | Array<any> | Date | Moment> extends EnpsComponent implements OnDestroy, AfterViewInit {
    appService = inject(AppService);
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    ngZone = inject(NgZone);
    changeDetectorRef = inject(ChangeDetectorRef);
    elmRef = inject(ElementRef);
    languageSettings = inject(LanguageSettings);
    visualStateService = inject(VisualStateService);
    dateLocale = this.store.selectSignal(selectDateLocale);

    value = input<T>(null);
    placeholder = input<string>('');
    label = input<string>('');
    hint = input<string>('');
    error = input<string>('');
    appearance = input<string>('outline');
    maxLength = input<number, any>(524288, {
        transform: val => coerceNumberProperty(val, 524288),
    });
    hasPrefix = input(false, {
        transform: coerceBooleanProperty,
    });
    hasSuffix = input(false, {
        transform: coerceBooleanProperty,
    });
    hasError = input(false, {
        transform: coerceBooleanProperty,
    });
    asCustomField = input(false, {
        transform: coerceBooleanProperty,
    });
    disabled = input(false, {
        transform: coerceBooleanProperty,
    });

    changed = output<any>();
    onMenuOpen = output<void>();

    searchBox = viewChild('searchBox', { read: ElementRef });
    internalControl = viewChildren<MatFormFieldControl<MatInput | MatChipGrid | MatSelect>>(MatFormFieldControl);

    isValueChanged = signal(false);

    destroyed$ = new Subject<void>();

    @HostBinding('attr.dir')
    get dir(): Direction {
        return this.globalTablesService.getRtl();
    }

    // TODO This needs to become computed
    locale = computed(() => {
        if (this.dateLocale()) {
            return this.dateLocale().replace(/(..)$/, '-$1');
        }
        if (this.appService.visualStateService.ConfigSettings.BaseLanguage) {
            if (this.appService.visualStateService.ConfigSettings.BaseLanguage.length > 2) {
                return this.appService.visualStateService.ConfigSettings.BaseLanguage.replace(/(..)$/, '-$1');
            }
            return this.appService.visualStateService.ConfigSettings.BaseLanguage;
        }
        return window.navigator.languages[0];
    });

    constructor() {
        super();
        effect(
            () => {
                this._setDisabledState(this.disabled());
                this._syncValueIfNecessary(this.value());
            },
            { allowSignalWrites: true }
        );
    }

    ngAfterViewInit() {
        this._updateInvalidState();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private _updateInvalidState() {
        if (this.required()) {
            this.internalControl()?.forEach(c => c.ngControl?.control?.markAllAsTouched());
            this._addValidatorIfNecessary();
        }
    }

    protected _addValidatorIfNecessary() {}
    protected _syncValueIfNecessary(_value: T) {}
    protected _setDisabledState(_val: boolean): void {}

    public onMenuOpened() {
        this.onMenuOpen.emit();
        if (this.searchBox()) {
            this.searchBox().nativeElement.focus();
        }
    }
}
